/**
 * Single source of truth for SAQ+C (conditioning) pace options and their
 * percentage adjustments.
 *
 * The pace names below are used verbatim as the value stored on an item's
 * `pacing` field and sent to the API. The percentages are TeamBuildr's
 * percentage-of-PR pacing adjustments and are display-only:
 *   - Run pacing (Speed/Interval/Threshold/Easy/Current PR) is based on the 1-mile PR.
 *   - Row pacing (Recovery, Nk Target) is based on the 2k row PR.
 *
 * Consumers:
 *   - SwapPropDropdownSAQC.js imports PACE_MENU_ITEMS (includes the non-selectable
 *     `Running`/`Rowing` group headers).
 *   - PacePickerPopup.js imports PACE_OPTIONS (selectable options only).
 * Keeping the lists here prevents the two dropdowns and the percentage map from
 * drifting apart.
 */

/** Run pacing options, in display order (based on 1 Mile PR). */
export const RUN_PACE_OPTIONS = [
  'Speed-1',
  'Speed-2',
  'Speed-3',
  'Current PR',
  'Interval-1',
  'Interval-2',
  'Interval-3',
  'Threshold-1',
  'Threshold-2',
  'Threshold-3',
  'Easy-1',
  'Easy-2',
  'Easy-3',
];

/** Row pacing options, in display order (based on 2k Row PR). */
export const ROW_PACE_OPTIONS = [
  'Recovery',
  '12k Target',
  '10k Target',
  '8k Target',
  '6k Target',
  '5k Target',
  '4k Target',
  '3k Target',
  '2k Target',
  '1k Target',
];

/** Non-selectable group header labels used as dividers in the pace dropdown. */
export const RUN_PACE_HEADER = 'Running';
export const ROW_PACE_HEADER = 'Rowing';

/** Flat list of selectable pace options (no group headers). */
export const PACE_OPTIONS = [...RUN_PACE_OPTIONS, ...ROW_PACE_OPTIONS];

/** Pace dropdown enum including the non-selectable `Running`/`Rowing` headers. */
export const PACE_MENU_ITEMS = [
  RUN_PACE_HEADER,
  ...RUN_PACE_OPTIONS,
  ROW_PACE_HEADER,
  ...ROW_PACE_OPTIONS,
];

/**
 * Maps each selectable pace name to its percentage adjustment (as a string).
 * Group headers intentionally have no entry.
 */
export const PACE_PERCENTAGES = {
  // Run pacing (based on 1 Mile PR)
  'Speed-1': '121.64',
  'Speed-2': '110.78',
  'Speed-3': '105.29',
  'Current PR': '100.00',
  'Interval-1': '94.32',
  'Interval-2': '89.51',
  'Interval-3': '86.91',
  'Threshold-1': '86.21',
  'Threshold-2': '85.07',
  'Threshold-3': '81.52',
  'Easy-1': '77.53',
  'Easy-2': '70.31',
  'Easy-3': '62.85',
  // Row pacing (based on 2k Row PR)
  Recovery: '32.32',
  '12k Target': '28.77',
  '10k Target': '28.40',
  '8k Target': '28.15',
  '6k Target': '27.57',
  '5k Target': '27.12',
  '4k Target': '26.77',
  '3k Target': '25.94',
  '2k Target': '25.02',
  '1k Target': '23.25',
};

/**
 * Formats a pace name for display, appending its percentage adjustment when one
 * exists (e.g. `Speed-1` -> `Speed-1 (121.64%)`). Names without a mapped
 * percentage — group headers, empty/null, or any legacy/unknown value — are
 * returned unchanged.
 *
 * @param {string} name The plain pace name.
 * @returns {string} The display label.
 */
export const formatPaceLabel = (name) => (
  PACE_PERCENTAGES[name] ? `${name} (${PACE_PERCENTAGES[name]}%)` : name
);
